import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';

interface MapLoadingContextType {
  isMapReady: boolean;
  setIsMapReady: React.Dispatch<React.SetStateAction<boolean>>;
  isAssetsLoading: boolean;
  setIsAssetsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  hasAssetsError: boolean;
  setHasAssetsError: React.Dispatch<React.SetStateAction<boolean>>;
}

const MapLoadingContext = createContext<MapLoadingContextType | null>(null);

export const useMapLoading = () => {
  const context = useContext(MapLoadingContext);
  if (!context) {
    throw new Error('useMapLoading must be used within a MapLoadingProvider');
  }
  return context;
};

// Optional hook that returns null if not within provider (for components that may or may not be in map context)
export const useMapLoadingOptional = () => useContext(MapLoadingContext);

interface MapLoadingProviderProps {
  children: ReactNode;
}

export const MapLoadingProvider: React.FC<MapLoadingProviderProps> = ({ children }) => {
  const [isMapReady, setIsMapReady] = useState<boolean>(false);
  const [isAssetsLoading, setIsAssetsLoading] = useState<boolean>(false);
  const [hasAssetsError, setHasAssetsError] = useState<boolean>(false);

  const contextValue = useMemo(
    () => ({
      isMapReady,
      setIsMapReady,
      isAssetsLoading,
      setIsAssetsLoading,
      hasAssetsError,
      setHasAssetsError,
    }),
    [isMapReady, isAssetsLoading, hasAssetsError]
  );

  return <MapLoadingContext.Provider value={contextValue}>{children}</MapLoadingContext.Provider>;
};

export default MapLoadingContext;
